// LegalPage.js
// Unified legal & compliance page — all 9 legal sections in one scrollable page.
// Route: /legal
// Deep-link anchors (via URL hash):
//   #disclaimer | #disclosure | #investor-charter | #complaints-data
//   #grievance-redressal | #news-policy | #refund-policy
//   #privacy-policy | #terms-and-conditions

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Container, Row, Col, Card, Table, Spinner, Alert,
} from 'react-bootstrap';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import './complaintData.css';   // complaints / grievance table classes
import './LegalPage.css';

// ── Section manifest ───────────────────────────────────────────────────────────
const SECTIONS = [
    { id: 'disclaimer',           label: 'Disclaimer' },
    { id: 'disclosure',           label: 'Disclosures' },
    { id: 'investor-charter',     label: 'Investor Charter' },
    { id: 'complaints-data',      label: 'Complaints Data' },
    { id: 'grievance-redressal',  label: 'Grievance Redressal' },
    { id: 'news-policy',          label: 'News & Research Policy' },
    { id: 'refund-policy',        label: 'Refund Policy' },
    { id: 'privacy-policy',       label: 'Privacy Policy' },
    { id: 'terms-and-conditions', label: 'Terms & Conditions' },
];

// ── Complaints data helpers ────────────────────────────────────────────────────
const calcComplaintTotals = (data) => {
    if (!data || data.length === 0) return {};
    return {
        Pending_at_the_end_of_last_month:   data.reduce((a, i) => a + (i.Pending_at_the_end_of_last_month   || 0), 0),
        Received:                            data.reduce((a, i) => a + (i.Received                            || 0), 0),
        Resolved:                            data.reduce((a, i) => a + (i.Resolved                            || 0), 0),
        Total_Pending:                       data.reduce((a, i) => a + (i.Total_Pending                       || 0), 0),
        Pending_Complaints_Over_3_months:    data.reduce((a, i) => a + (i.Pending_Complaints_Over_3_months    || 0), 0),
    };
};
const calcTrendTotals = (data) => {
    if (!data || data.length === 0) return {};
    return {
        carried_forward: data.reduce((a, i) => a + (i.carried_forward || 0), 0),
        received:        data.reduce((a, i) => a + (i.received        || 0), 0),
        resolved:        data.reduce((a, i) => a + (i.resolved        || 0), 0),
        pending:         data.reduce((a, i) => a + (i.pending         || 0), 0),
    };
};

// ── Component ──────────────────────────────────────────────────────────────────
const LegalPage = () => {
    const { hash } = useLocation();
    const [activeId, setActiveId] = useState(SECTIONS[0].id);

    // Scroll to the hashed section on mount / hash change
    useEffect(() => {
        const id = hash ? hash.replace('#', '') : '';
        if (id) {
            setActiveId(id);
            const timer = setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 80);
            return () => clearTimeout(timer);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [hash]);

    // Complaints Data — Firestore fetch
    const [cData, setCData]       = useState(null);
    const [cLoading, setCLoading] = useState(true);
    const [cError, setCError]     = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'legalPages', 'complaintsData'));
                if (snap.exists()) setCData(snap.data());
                else setCError('No complaints data found.');
            } catch {
                setCError('Failed to load complaints data. Please try again later.');
            } finally {
                setCLoading(false);
            }
        })();
    }, []);

    // Complaint totals (computed only when data is ready)
    const complaintTotals = cData ? calcComplaintTotals(cData.ComplaintData)    : {};
    const monthlyTotals   = cData ? calcTrendTotals(cData.trendData)            : {};
    const annualTotals    = cData ? calcTrendTotals(cData.annualTrendData)      : {};

    return (
        <div className="legal-page">
            {/* ── Page header ─────────────────────────────────────────────── */}
            <div className="legal-page-header">
                <h1 className="legal-page-title">Legal &amp; Compliance</h1>
                <p className="legal-page-subtitle">
                    Regulatory disclosures, policies, and investor information for
                    Right Spade Capital Advisors Pvt. Ltd. — SEBI Registered Research Analyst
                    (INH000018799)
                </p>
            </div>

            <Container fluid="lg" className="legal-page-body">
                <Row>
                    {/* ── Sticky sidebar (desktop) ─────────────────────────── */}
                    <Col lg={3} className="d-none d-lg-block">
                        {/* Named to distinguish it from the primary navigation (audit round-2) */}
                        <nav className="legal-sidebar" aria-label="Legal sections">
                            <p className="legal-sidebar-title">On this page</p>
                            <ul className="legal-sidebar-list">
                                {SECTIONS.map((s) => (
                                    <li key={s.id}>
                                        <a
                                            href={`#${s.id}`}
                                            className={`legal-sidebar-link${activeId === s.id ? ' active' : ''}`}
                                            onClick={() => setActiveId(s.id)}
                                        >
                                            {s.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </Col>

                    {/* ── Main content ─────────────────────────────────────── */}
                    <Col lg={9}>
                        {/* Mobile jump-to */}
                        <div className="legal-mobile-toc d-lg-none">
                            <select
                                className="legal-mobile-select"
                                aria-label="Jump to legal section"
                                value={activeId}
                                onChange={(e) => {
                                    const id = e.target.value;
                                    setActiveId(id);
                                    const el = document.getElementById(id);
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            >
                                {SECTIONS.map((s) => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* ══════════════════════════════════════════════════
                            1. DISCLAIMER
                        ══════════════════════════════════════════════════ */}
                        <section id="disclaimer" className="legal-section">
                            <h2 className="legal-section-title">Disclaimer</h2>
                            <p className="ls-intro">
                                This website and all research publications are prepared by Rightspade Capital
                                Advisors Pvt Ltd, a SEBI Registered Research Analyst (Reg. No. INH000018799).
                            </p>

                            <h3 className="ls-h3">General Information</h3>
                            <p className="ls-p">
                                The information contained in our publications is based on publicly available
                                data and sources we believe to be reliable. However, we do not guarantee its
                                completeness, accuracy, or reliability.
                            </p>

                            <h3 className="ls-h3">Research Analyst Interests</h3>
                            <p className="ls-p">
                                Research analysts responsible for our publications may have financial interests
                                in securities mentioned, and their views may be influenced by such interests.
                            </p>

                            <h3 className="ls-h3">Conflicts of Interest</h3>
                            <p className="ls-p">
                                Rightspade Capital Advisors Pvt Ltd may have existing or potential
                                relationships with companies covered in our publications, which could
                                represent possible conflicts of interest. These will be disclosed as required
                                under SEBI regulations.
                            </p>

                            <h3 className="ls-h3">Investment Risks</h3>
                            <p className="ls-p">
                                Investing in securities is subject to risks, including potential loss of
                                capital. Past performance does not guarantee future results. Investors should
                                conduct their own due diligence and seek advice from independent financial
                                advisors before making investment decisions.
                            </p>

                            <h3 className="ls-h3">No Liability</h3>
                            <p className="ls-p">
                                Rightspade Capital Advisors Pvt Ltd shall not be liable for any direct,
                                indirect, or consequential losses or damages arising from the use of our
                                website, publications, or reliance on the information provided therein.
                            </p>

                            <h3 className="ls-h3">Confidentiality</h3>
                            <p className="ls-p">
                                The content and publications on this website are intended for the exclusive
                                use of clients and subscribers. Reproduction, redistribution, or transmission
                                of any material without prior written consent is strictly prohibited.
                            </p>

                            <h3 className="ls-h3">Regulatory Compliance</h3>
                            <p className="ls-p">
                                All publications and communications comply with applicable SEBI laws and
                                regulations.
                            </p>

                            <h3 className="ls-h3">Contact Information</h3>
                            <p className="ls-p">
                                For further information regarding our research policies and conflict of
                                interest management, please contact us at{' '}
                                <a href="mailto:care@rightspade.com" className="ls-link">care@rightspade.com</a>.
                            </p>

                            <h3 className="ls-h3">Definitions <small style={{ fontWeight: 400, fontSize: '0.85em', color: '#6b7280' }}>(Terms used across research publications)</small></h3>
                            {[
                                ['BUY', 'A recommendation to purchase a security, indicating a positive outlook on its potential performance.'],
                                ['SELL', 'A recommendation to dispose of a security, suggesting limited potential for further gains or risk of loss.'],
                                ['HOLD', 'A recommendation to retain a security, indicating a neutral outlook where it is expected to perform in line with the market.'],
                                ['TRIGGER PRICE', 'The price at which a trade is activated, confirming the entry or exit as per the recommendation.'],
                                ['CURRENT PRICE', 'The market price of the security at the time of issuing the recommendation.'],
                                ['TARGET (EXIT)', 'The price levels at which the security is recommended to be sold to realise expected gains. Multiple targets represent staggered exit levels.'],
                                ['STOP LOSS (EXIT)', 'A predetermined price below the buying price to minimise losses in case the security moves unfavourably.'],
                                ['BUYING RANGE', 'The price range within which the security is recommended for purchase to achieve the stated target.'],
                                ['HOLDING PERIOD', 'The expected duration to hold the security in order to achieve the target or objective.'],
                            ].map(([term, def]) => (
                                <div className="ls-def" key={term}>
                                    <span className="ls-def-term">{term}: </span>{def}
                                </div>
                            ))}
                        </section>

                        {/* ══════════════════════════════════════════════════
                            2. DISCLOSURES
                        ══════════════════════════════════════════════════ */}
                        <section id="disclosure" className="legal-section">
                            <h2 className="legal-section-title">Disclosures</h2>

                            <h3 className="ls-h3">Ownership and Material Conflicts of Interest</h3>
                            <ul className="ls-ul">
                                <li>Neither the Company nor its associates have any financial interest in any of the companies covered in our research publications.</li>
                                <li>Neither the research analysts nor their relatives have any financial interest in any of the companies covered in our publications.</li>
                                <li>Neither the research analysts, the Company, nor its associates hold actual/beneficial ownership of one percent or more in the securities of any company covered in our research publications, as of the end of the month immediately preceding such publication.</li>
                                <li>Neither the research analysts nor their relatives have any other material conflict of interest at the time of publication of any research content.</li>
                                <li>Neither the Company nor its associates have any other material conflict of interest at the time of publication of any research content.</li>
                            </ul>

                            <h3 className="ls-h3">Receipt of Compensation</h3>
                            <ul className="ls-ul">
                                <li>The Company or its associates have not received any compensation from any company covered in our research publications in the past twelve months.</li>
                                <li>The Company or its associates have not managed or co-managed any public offering of securities for companies covered in our publications in the past twelve months.</li>
                                <li>The Company or its associates have not received any compensation for investment banking, merchant banking, or brokerage services from any company covered in our publications in the past twelve months.</li>
                                <li>The Company or its associates have not received any compensation for products or services other than investment banking, merchant banking, or brokerage services from any company covered in our publications in the past twelve months.</li>
                                <li>The Company or its associates have not received any compensation or other benefits from any company or third party in connection with our research publications or recommendations made to clients.</li>
                            </ul>

                            <h3 className="ls-h3">Other Disclosures</h3>
                            <ul className="ls-ul">
                                <li>The Research Analysts of the Company have not served as an officer, director, or employee of any company covered in our publications.</li>
                                <li>The Research Analysts or the Company have not been engaged in market-making activity for any of the companies covered in our publications.</li>
                                <li>The Company uses Artificial Intelligence (AI) tools to identify a preliminary set of securities based on backtested strategies. These tools assist in filtering securities with strong fundamental and technical characteristics. AI is also used to enhance communication and aid users in interpreting research content more effectively. All final recommendations are reviewed and approved by SEBI-registered and qualified Research Analysts.</li>
                            </ul>

                            <h3 className="ls-h3">Contact Information</h3>
                            <p className="ls-p">
                                If you require additional information regarding analyst interests, ownership,
                                or any other disclosures under SEBI regulations, you may contact us at{' '}
                                <a href="mailto:care@rightspade.com" className="ls-link">care@rightspade.com</a>.
                            </p>
                        </section>

                        {/* ══════════════════════════════════════════════════
                            3. INVESTOR CHARTER
                        ══════════════════════════════════════════════════ */}
                        <section id="investor-charter" className="legal-section">
                            <h2 className="legal-section-title">Investor Charter</h2>
                            <p className="ls-intro">
                                Investor Charter in Respect of Research Analysts (RAs)
                            </p>

                            <h3 className="ls-h3">A. Vision and Mission Statements for Investors</h3>
                            <ul className="ls-ul">
                                <li><strong>Vision:</strong> Invest with knowledge &amp; safety.</li>
                                <li><strong>Mission:</strong> Every investor should be able to invest in the right investment products based on their needs, manage and monitor them to meet their goals, access reports, and enjoy financial wellness.</li>
                            </ul>

                            <h3 className="ls-h3">B. Details of Business Transacted by the Research Analyst with Investors</h3>
                            <ul className="ls-ul">
                                <li>Publish research reports based on the RA's research activities</li>
                                <li>Provide an independent and unbiased view on securities</li>
                                <li>Offer unbiased recommendations, disclosing financial interests in recommended securities</li>
                                <li>Base research recommendations on publicly available information and known observations</li>
                                <li>Conduct annual audits</li>
                                <li>Ensure advertisements comply with the Advertisement Code for Research Analysts</li>
                                <li>Maintain records of all client interactions, including those prior to onboarding</li>
                            </ul>

                            <h3 className="ls-h3">C. Services Provided to Investors (No Indicative Timelines)</h3>
                            <h4 className="ls-h4">Client Onboarding</h4>
                            <ul className="ls-ul">
                                <li>Share terms and conditions of research services</li>
                                <li>Complete KYC for fee-paying clients</li>
                            </ul>
                            <h4 className="ls-h4">Disclosure to Clients</h4>
                            <ul className="ls-ul">
                                <li>Material information to aid informed decision-making</li>
                                <li>Use of Artificial Intelligence tools in research services</li>
                                <li>Conflicts of interest (own or third-party)</li>
                                <li>Details of business activities, associates, and disciplinary history</li>
                                <li>Distribution of reports without discrimination</li>
                                <li>Maintain confidentiality until reports are in public domain</li>
                                <li>Ensure data privacy and unauthorised information protection</li>
                                <li>Disclose service timelines and adhere to them</li>
                                <li>Provide clear guidance on complex and high-risk products</li>
                                <li>Treat all clients with honesty and integrity</li>
                                <li>Maintain confidentiality unless legally obligated or with client consent</li>
                            </ul>

                            <h3 className="ls-h3">D. Grievance Redressal Mechanism</h3>
                            <ol className="ls-ol">
                                <li>Filing a Complaint with Research Analyst: Grievance must be resolved within 21 days.</li>
                                <li>
                                    SCORES 2.0 (<a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="ls-link">https://scores.sebi.gov.in</a>):
                                    <ul className="ls-ul" style={{ marginTop: '0.25rem' }}>
                                        <li>First-level review: RAASB</li>
                                        <li>Second-level review: SEBI</li>
                                    </ul>
                                </li>
                                <li>SMARTODR Platform: Online conciliation or arbitration</li>
                                <li>
                                    Physical Complaint Submission:<br />
                                    Office of Investor Assistance and Education, SEBI Bhavan, Plot No. C4-A,
                                    'G' Block, Bandra-Kurla Complex, Bandra (E), Mumbai – 400 051
                                </li>
                            </ol>

                            <h3 className="ls-h3">E. Rights of Investors</h3>
                            <ul className="ls-ul">
                                <li>Right to Privacy and Confidentiality</li>
                                <li>Right to Transparent Practices</li>
                                <li>Right to Fair and Equitable Treatment</li>
                                <li>Right to Adequate Information</li>
                                <li>Right to Disclosures (Initial and Continuing)</li>
                                <li>Right to True and Fair Advertisement</li>
                                <li>Right to know Service Parameters and Timelines</li>
                                <li>Right to be Heard and receive Grievance Redressal</li>
                                <li>Right to Exit financial services as per terms</li>
                                <li>Right to receive Caution Notices for high-risk products</li>
                                <li>Right to Accessible Services (especially for vulnerable/differently abled consumers)</li>
                                <li>Right to Feedback</li>
                                <li>Right against Coercive or Unfair Clauses</li>
                            </ul>

                            <h3 className="ls-h3">F. Responsibilities of Investors</h3>
                            <h4 className="ls-h4">Do's</h4>
                            <ol className="ls-ol">
                                <li>Deal only with SEBI-registered RAs</li>
                                <li>Verify RA's SEBI registration number</li>
                                <li>Read disclosures in reports carefully before investing</li>
                                <li>Make payments via banking channels and keep signed receipts</li>
                                <li>Use CeFCoM if applicable</li>
                                <li>Verify RA recommendations before investing</li>
                                <li>Ask relevant questions before acting on any recommendation</li>
                                <li>Seek clarity on high-risk/complex products</li>
                                <li>Know your right to discontinue RA services</li>
                                <li>Provide feedback on services</li>
                                <li>Refuse any clauses violating regulatory provisions</li>
                                <li>Report RAs promising assured or guaranteed returns to SEBI</li>
                            </ol>
                            <h4 className="ls-h4" style={{ marginTop: '1rem' }}>Don'ts</h4>
                            <ol className="ls-ol">
                                <li>Do not give funds to the RA for investment</li>
                                <li>Do not fall for luring ads or rumors</li>
                                <li>Do not be influenced by limited-time offers, discounts, or gifts</li>
                                <li>Do not share login credentials of your demat/trading/bank accounts with the RA</li>
                            </ol>
                        </section>

                        {/* ══════════════════════════════════════════════════
                            4. COMPLAINTS DATA
                        ══════════════════════════════════════════════════ */}
                        <section id="complaints-data" className="legal-section">
                            <h2 className="legal-section-title">Complaints Data</h2>

                            {cLoading && (
                                <div className="d-flex align-items-center gap-2 py-3">
                                    <Spinner animation="border" size="sm" style={{ color: '#3871E0' }} />
                                    <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Loading data…</span>
                                </div>
                            )}
                            {!cLoading && cError && (
                                <Alert variant="danger" style={{ fontSize: '0.875rem' }}>{cError}</Alert>
                            )}
                            {!cLoading && !cError && cData && (
                                <div className="table-wrap">
                                    {/* Monthly complaints */}
                                    <div>
                                        <p className="ls-card-title">{cData.reportTitle || 'Data for the month'}</p>
                                        <Card className="complaints-data-card">
                                            <Card.Body>
                                                <Table responsive>
                                                    <caption className="visually-hidden">{cData.reportTitle || 'Data for the month'}</caption>
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">SI No</th>
                                                            <th scope="col">Received from</th>
                                                            <th scope="col">Pending at end of last month</th>
                                                            <th scope="col">Received</th>
                                                            <th scope="col">Resolved*</th>
                                                            <th scope="col">Total Pending #</th>
                                                            <th scope="col">Pending &gt; 3 months</th>
                                                            <th scope="col">Avg Resolution (days)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cData.ComplaintData?.map((item, i) => (
                                                            <tr key={i}>
                                                                <td>{item.SI_No}</td>
                                                                <td>{item.Received_from}</td>
                                                                <td>{item.Pending_at_the_end_of_last_month}</td>
                                                                <td>{item.Received}</td>
                                                                <td>{item.Resolved}</td>
                                                                <td>{item.Total_Pending}</td>
                                                                <td>{item.Pending_Complaints_Over_3_months}</td>
                                                                <td>{item.Average_Resolution_Time}</td>
                                                            </tr>
                                                        ))}
                                                        <tr className="ls-grand-total">
                                                            <td></td>
                                                            <td>Grand Total</td>
                                                            <td>{complaintTotals.Pending_at_the_end_of_last_month}</td>
                                                            <td>{complaintTotals.Received}</td>
                                                            <td>{complaintTotals.Resolved}</td>
                                                            <td>{complaintTotals.Total_Pending}</td>
                                                            <td>{complaintTotals.Pending_Complaints_Over_3_months}</td>
                                                            <td></td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    </div>

                                    {/* Monthly trend */}
                                    <div>
                                        <p className="ls-card-title">Trend of monthly disposal of complaints</p>
                                        <Card className="complaints-data-card">
                                            <Card.Body>
                                                <Table responsive>
                                                    <caption className="visually-hidden">Trend of monthly disposal of complaints</caption>
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">SI No</th>
                                                            <th scope="col">Month</th>
                                                            <th scope="col">Carried forward from previous month</th>
                                                            <th scope="col">Received</th>
                                                            <th scope="col">Resolved*</th>
                                                            <th scope="col">Pending #</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cData.trendData?.map((item, i) => (
                                                            <tr key={i}>
                                                                <td>{item.sl_no}</td>
                                                                <td>{item.month}</td>
                                                                <td>{item.carried_forward}</td>
                                                                <td>{item.received}</td>
                                                                <td>{item.resolved}</td>
                                                                <td>{item.pending}</td>
                                                            </tr>
                                                        ))}
                                                        <tr className="ls-grand-total">
                                                            <td></td>
                                                            <td>Grand Total</td>
                                                            <td>{monthlyTotals.carried_forward}</td>
                                                            <td>{monthlyTotals.received}</td>
                                                            <td>{monthlyTotals.resolved}</td>
                                                            <td>{monthlyTotals.pending}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    </div>

                                    {/* Annual trend */}
                                    <div>
                                        <p className="ls-card-title">Trend of annual disposal of complaints</p>
                                        <Card className="complaints-data-card">
                                            <Card.Body>
                                                <Table responsive>
                                                    <caption className="visually-hidden">Trend of annual disposal of complaints</caption>
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">SI No</th>
                                                            <th scope="col">Year</th>
                                                            <th scope="col">Carried forward from previous year</th>
                                                            <th scope="col">Received</th>
                                                            <th scope="col">Resolved*</th>
                                                            <th scope="col">Pending #</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cData.annualTrendData?.map((item, i) => (
                                                            <tr key={i}>
                                                                <td>{item.sl_no}</td>
                                                                <td>{item.year}</td>
                                                                <td>{item.carried_forward}</td>
                                                                <td>{item.received}</td>
                                                                <td>{item.resolved}</td>
                                                                <td>{item.pending}</td>
                                                            </tr>
                                                        ))}
                                                        <tr className="ls-grand-total">
                                                            <td></td>
                                                            <td>Grand Total</td>
                                                            <td>{annualTotals.carried_forward}</td>
                                                            <td>{annualTotals.received}</td>
                                                            <td>{annualTotals.resolved}</td>
                                                            <td>{annualTotals.pending}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    </div>

                                    {/* Escalation Matrix */}
                                    <div>
                                        <p className="ls-card-title">Escalation Matrix</p>
                                        <Card className="complaints-data-card">
                                            <Card.Body>
                                                <Table responsive>
                                                    <caption className="visually-hidden">Escalation matrix</caption>
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">Details of Designation</th>
                                                            <th scope="col">Contact Person</th>
                                                            <th scope="col">Address</th>
                                                            <th scope="col">Contact No.</th>
                                                            <th scope="col">Email</th>
                                                            <th scope="col">Working Hours</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cData.escalationData?.map((item, i) => (
                                                            <tr key={i}>
                                                                <td>{item.designation}</td>
                                                                <td>{item.contact_person}</td>
                                                                <td>{item.address}</td>
                                                                <td>{item.contact_no}</td>
                                                                <td>{item.email}</td>
                                                                <td>{item.working_hours}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* ══════════════════════════════════════════════════
                            5. GRIEVANCE REDRESSAL
                        ══════════════════════════════════════════════════ */}
                        <section id="grievance-redressal" className="legal-section">
                            <h2 className="legal-section-title">Accessibility &amp; Grievance Redressal</h2>

                            <div className="mb-3">
                                <a
                                    href="/query?type=grievance"
                                    className="ls-grievance-btn"
                                >
                                    Submit Grievance — Contact Us
                                </a>
                            </div>

                            <p className="ls-card-title">Contact Information for Grievance Officers</p>
                            <Card className="complaints-data-card mb-4">
                                <Card.Body>
                                    <Table responsive>
                                        <caption className="visually-hidden">Contact information for grievance officers</caption>
                                        <thead>
                                            <tr>
                                                <th scope="col">Details of Designation</th>
                                                <th scope="col">Contact Person Name</th>
                                                <th scope="col">Address</th>
                                                <th scope="col">Contact No.</th>
                                                <th scope="col">Email</th>
                                                <th scope="col">Note</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                {
                                                    designation: 'Accessibility Nodal Officer',
                                                    contactPerson: 'Shreyas Pandit – Director – Compliance Officer',
                                                    address: '1008, 3rd Floor, 7th A Main, Koramangala 1st Block, Bengaluru 560034',
                                                    contactNo: '+91-9845004645',
                                                    email: 'shreyas@rightspade.com',
                                                    note: 'Mon to Friday, 10:00 am to 4:00 pm',
                                                },
                                                {
                                                    designation: 'Grievance Redressal (Escalation)',
                                                    contactPerson: 'Ritu Sharma – Director – Principal Officer',
                                                    address: '1008, 3rd Floor, 7th A Main, Koramangala 1st Block, Bengaluru 560034',
                                                    contactNo: '+91-9972168800',
                                                    email: 'ritu@rightspade.com',
                                                    note: 'Mon to Friday, 10:00 am to 4:00 pm',
                                                },
                                            ].map((o, i) => (
                                                <tr key={i}>
                                                    <td>{o.designation}</td>
                                                    <td>{o.contactPerson}</td>
                                                    <td>{o.address}</td>
                                                    <td>{o.contactNo}</td>
                                                    <td><a href={`mailto:${o.email}`} className="email-link">{o.email}</a></td>
                                                    <td>{o.note}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>

                            <p className="ls-card-title">Grievance Handling Process</p>
                            <Card className="complaints-data-card">
                                <Card.Body>
                                    <Table responsive>
                                        <caption className="visually-hidden">Grievance handling process</caption>
                                        <thead>
                                            <tr>
                                                <th scope="col">Step</th>
                                                <th scope="col">Stage</th>
                                                <th scope="col">Action</th>
                                                <th scope="col">Turnaround Time (TAT)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { step: '1', stage: 'Report',          action: 'Click "Grievance – Contact Us" and select "Grievance Redressal & Escalation" in the dropdown, or send an email / call the helpline (details in table above).', tat: '' },
                                                { step: '2', stage: 'Acknowledgement', action: 'Confirmation sent with a reference number.', tat: 'Within 24 hours' },
                                                { step: '3', stage: 'Review',          action: 'Nodal Officer reviews grievance.', tat: 'Within 3 working days' },
                                                { step: '4', stage: 'Resolution',      action: 'Minor issues (billing clarification, accessible reports) → resolved. Major/system issues (platform upgrades, service changes) → resolved with interim workaround if needed.', tat: 'Minor: 7 working days\nMajor: 30 working days' },
                                                { step: '5', stage: 'Escalation',      action: 'If unresolved or unsatisfactory, grievance escalated to Grievance Redressal Officer.', tat: 'Response within 7 working days' },
                                                { step: '6', stage: 'Closure',         action: 'Written resolution shared with client. Feedback option provided before closure.', tat: '' },
                                            ].map((row, i) => (
                                                <tr key={i}>
                                                    <td className="step-number">{row.step}</td>
                                                    <td className="stage-name">{row.stage}</td>
                                                    <td className="action-description">
                                                        {row.action.split('\n').map((line, j) => <div key={j}>{line}</div>)}
                                                    </td>
                                                    <td className="tat-info">
                                                        {row.tat
                                                            ? row.tat.split('\n').map((line, j) => <div key={j}>{line}</div>)
                                                            : '–'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </section>

                        {/* ══════════════════════════════════════════════════
                            6. NEWS & RESEARCH POLICY
                        ══════════════════════════════════════════════════ */}
                        <section id="news-policy" className="legal-section">
                            <h2 className="legal-section-title">Policy on Prevention of Circulation of Unauthenticated News</h2>
                            <p className="ls-date">7th July 2025</p>

                            <h3 className="ls-h3">1. Purpose</h3>
                            <p className="ls-p">
                                This policy establishes guidelines and controls to prevent the circulation of
                                unauthenticated news or rumors by employees, contractors, and representatives of
                                Rightspade Capital Advisors Private Limited INH000018799, in compliance with SEBI
                                Circular No. CIR/ISD/1/2011 dated March 23, 2011.
                            </p>

                            <h3 className="ls-h3">2. Scope</h3>
                            <p className="ls-p">
                                This policy applies to all employees, temporary staff, volunteers, and third-party
                                service providers of Rightspade Capital Advisors Private Limited, across all offices
                                and communication channels including email, social media, chat forums, and messenger
                                applications.
                            </p>

                            <h3 className="ls-h3">3. Definitions</h3>
                            <p className="ls-p"><strong>Unauthenticated News:</strong> Any market-related information or rumor not verified by an official source.</p>
                            <p className="ls-p">
                                <strong>Compliance Officer Details:</strong> The designated officer responsible for oversight of compliance matters:<br />
                                Shreyas Pandit, Director / Compliance Officer — +91-9845004645 / shreyas@rightspade.com
                            </p>
                            <p className="ls-p"><strong>Chat Forums / Messenger Sites:</strong> Public or private online platforms used for real-time messaging.</p>

                            <h3 className="ls-h3">4. Policy Statements</h3>
                            <h4 className="ls-h4">4.1 Internal Code of Conduct</h4>
                            <p className="ls-p">The code of conduct is given in Annexure A of this document.</p>
                            <h4 className="ls-h4">4.2 Controlled Access</h4>
                            <p className="ls-p">Restrict or supervise employee access to blogs, chat forums, and messenger sites. Unauthorised access is prohibited.</p>
                            <h4 className="ls-h4">4.3 Logging</h4>
                            <p className="ls-p">Maintain logs of access to and usage of blogs, chat forums, and messenger sites. Retain logs for a minimum of five years.</p>
                            <h4 className="ls-h4">4.4 Verification Requirement</h4>
                            <p className="ls-p">All market-related news received via any channel must be verified and approved by the Compliance Officer prior to circulation.</p>
                            <h4 className="ls-h4">4.5 Approval Workflow</h4>
                            <p className="ls-p">Employees must forward any received market information to the Compliance Officer. No forwarding or posting is allowed until written approval is obtained.</p>
                            <h4 className="ls-h4">4.6 Employee Awareness</h4>
                            <p className="ls-p">Ensure employees complete annual training on the risks of circulating unauthenticated news and this policy.</p>
                            <h4 className="ls-h4">4.7 Disciplinary Action</h4>
                            <p className="ls-p">Violations of this policy may result in disciplinary measures up to and including termination of employment.</p>

                            <h3 className="ls-h3">5. Roles and Responsibilities</h3>
                            <Card className="complaints-data-card mb-3">
                                <Card.Body>
                                    <Table responsive>
                                        <caption className="visually-hidden">Roles and responsibilities</caption>
                                        <thead><tr><th scope="col">Role</th><th scope="col">Responsibilities</th></tr></thead>
                                        <tbody>
                                            {[
                                                ['Employees',           'Comply with verification and approval procedures; report any unauthenticated news to Compliance Officer.'],
                                                ['Compliance Officer',  'Review and verify all market-related information; maintain approval records and logs.'],
                                                ['IT Department',       'Implement technical controls to restrict or monitor access to unauthorised sites; retain logs.'],
                                                ['HR Department',       'Ensure policy acknowledgment and arrange annual training for all employees.'],
                                            ].map(([role, resp], i) => (
                                                <tr key={i}><td>{role}</td><td>{resp}</td></tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>

                            <h3 className="ls-h3">6. Procedures</h3>
                            <ul className="ls-ul">
                                <li><strong>6.1 Receipt of Information:</strong> Employees must immediately forward any market-related news to the Compliance Officer.</li>
                                <li><strong>6.2 Verification:</strong> Compliance Officer confirms source authenticity using official announcements, company communications, or credible news agencies.</li>
                                <li><strong>6.3 Approval:</strong> Upon verification, Compliance Officer issues written approval to the requesting employee.</li>
                                <li><strong>6.4 Distribution:</strong> Approved information can then be circulated via appropriate internal or external channels.</li>
                                <li><strong>6.5 Record-Keeping:</strong> Compliance Officer files all approval requests and decisions in a central repository.</li>
                            </ul>

                            <h3 className="ls-h3">7. Record Retention</h3>
                            <p className="ls-p">Maintain all related records including logs, approval requests, and correspondence for a period of at least five (5) years or as required by applicable regulations.</p>

                            <h3 className="ls-h3">8. Policy Review</h3>
                            <p className="ls-p">This policy shall be reviewed annually, or sooner if required by regulatory changes, to ensure its effectiveness and compliance with SEBI guidelines.</p>

                            <h3 className="ls-h3">9. References</h3>
                            <ul className="ls-ul">
                                <li>SEBI Circular No. CIR/ISD/1/2011 dated March 23, 2011</li>
                                <li>SEBI Master Circular dated May 21, 2024 (Clause 9)</li>
                            </ul>

                            <hr className="ls-annexure-divider" />

                            <h3 className="ls-h3" style={{ textAlign: 'center' }}>Annexure A</h3>
                            <h4 className="ls-h4" style={{ textAlign: 'center', marginBottom: '1rem' }}>Code of Conduct for Verification of Market-Related Information</h4>

                            <h4 className="ls-h4">1. Introduction</h4>
                            <p className="ls-p">This Code of Conduct sets out the principles and procedures for verifying all market-related information before dissemination by Rightspade Capital Advisors Private Limited INH000018799, in line with Clause 4.1 of the Policy on Prevention of Circulation of Unauthenticated News.</p>

                            <h4 className="ls-h4">2. Scope</h4>
                            <p className="ls-p">This Code applies to all employees, contractors, consultants, and third-party service providers engaged in the creation, review, approval, or dissemination of market-related information.</p>

                            <h4 className="ls-h4">3. Core Principles</h4>
                            <ul className="ls-ul">
                                <li><strong>Accuracy:</strong> Ensure that information is complete, factual, and free from material error.</li>
                                <li><strong>Objectivity:</strong> Present market data and opinions impartially, without bias or undue influence.</li>
                                <li><strong>Transparency:</strong> Clearly disclose all sources, assumptions, and limitations of the information.</li>
                                <li><strong>Accountability:</strong> Individuals responsible for information verification must be identifiable and answerable.</li>
                            </ul>

                            <h4 className="ls-h4">4. Verification Procedures</h4>
                            <ul className="ls-ul">
                                <li><strong>4.1 Initial Assessment:</strong> Upon receipt or creation of market-related information, assess its origin and potential impact.</li>
                                <li><strong>4.2 Source Validation:</strong> Confirm the authenticity of information with at least two independent, credible sources.</li>
                                <li><strong>4.3 Documentation:</strong> Record verification steps, sources consulted, and outcomes in the Verification Log.</li>
                                <li><strong>4.4 Compliance Officer Review:</strong> Submit the Verification Log and information to the Compliance Officer for approval.</li>
                                <li><strong>4.5 Final Approval:</strong> Only after written approval from the Compliance Officer may information be disseminated.</li>
                            </ul>

                            <h4 className="ls-h4">5. Roles and Responsibilities</h4>
                            <Card className="complaints-data-card mb-3">
                                <Card.Body>
                                    <Table responsive>
                                        <caption className="visually-hidden">Roles and responsibilities</caption>
                                        <thead><tr><th scope="col">Role</th><th scope="col">Responsibility</th></tr></thead>
                                        <tbody>
                                            {[
                                                ['Information Creator',  'Initiate verification and maintain accuracy of content before submission.'],
                                                ['Information Reviewer', 'Conduct secondary review of content for consistency and completeness.'],
                                                ['Compliance Officer',   'Authorise dissemination and maintain the Verification Log.'],
                                                ['IT Support',           'Maintain secure systems for log retention and access control.'],
                                            ].map(([role, resp], i) => (
                                                <tr key={i}><td>{role}</td><td>{resp}</td></tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>

                            <h4 className="ls-h4">6. Training and Awareness</h4>
                            <p className="ls-p">All personnel covered by this Code shall complete training on verification procedures and this Code of Conduct upon joining and on an annual basis thereafter.</p>

                            <h4 className="ls-h4">7. Compliance and Enforcement</h4>
                            <p className="ls-p">Non-compliance with this Code may result in disciplinary action, up to and including termination of employment or contract, in accordance with Company policies and applicable laws.</p>

                            <h4 className="ls-h4">8. Amendment</h4>
                            <p className="ls-p">This Code may be reviewed and amended periodically by the Compliance Officer in consultation with senior management to reflect regulatory changes or operational needs.</p>
                        </section>

                        {/* ══════════════════════════════════════════════════
                            7. REFUND POLICY
                        ══════════════════════════════════════════════════ */}
                        <section id="refund-policy" className="legal-section">
                            <h2 className="legal-section-title">Refund Policy</h2>
                            <p className="ls-intro">
                                This Refund &amp; Termination Policy outlines the terms under which services may
                                be terminated and refunds may be issued by Rightspade Capital Advisors Pvt Ltd.
                                This policy should be read in conjunction with the Service Agreement and Terms &amp;
                                Conditions document.
                            </p>

                            <h3 className="ls-h3">1. Termination of Services</h3>
                            <ul className="ls-ul">
                                <li>Non-payment of fees beyond 30 days from the date of a formal reminder.</li>
                                <li>Expiry of the service period without renewal by the Client.</li>
                                <li>Suspension or cancellation of Rightspade's SEBI Research Analyst registration.</li>
                                <li>Bankruptcy or liquidation of either the Client or Rightspade.</li>
                                <li>Directive or enforcement action issued by any regulatory or government authority.</li>
                                <li>Fraudulent activity, misrepresentation, or money laundering concerns, as determined by Rightspade.</li>
                            </ul>

                            <h3 className="ls-h3">2. Refund of Fees</h3>
                            <ul className="ls-ul">
                                <li>Refunds will be issued on a pro rata basis for the unutilised portion of the service period, based on the reason for termination.</li>
                                <li>In the event of suspension of registration exceeding 60 days or cancellation of Rightspade's SEBI registration, a pro rata refund will be issued for the period from the effective date of such action until the end of the subscription term.</li>
                                <li>No refunds will be issued for completed service periods or if termination arises due to client-side violations such as fraud or misrepresentation.</li>
                            </ul>
                            <p className="ls-p">All refund requests must be submitted in writing and will be processed in accordance with this policy and applicable laws.</p>
                            <p className="ls-p">
                                If you have any questions, please write to us at{' '}
                                <a href="mailto:care@rightspade.com" className="ls-link">care@rightspade.com</a>
                            </p>
                        </section>

                        {/* ══════════════════════════════════════════════════
                            8. PRIVACY POLICY
                        ══════════════════════════════════════════════════ */}
                        <section id="privacy-policy" className="legal-section">
                            <h2 className="legal-section-title">Privacy Policy</h2>
                            <p className="ls-intro">
                                Right Spade Capital Advisors respects your privacy and is committed to
                                safeguarding the personal and sensitive information you share with us. This
                                Privacy Policy outlines how we collect, use, disclose, and protect your
                                information in compliance with the Information Technology Act, 2000, the IT
                                Rules, 2011, and other applicable Indian laws.
                            </p>

                            <h3 className="ls-h3">1. Applicability</h3>
                            <p className="ls-p">This Privacy Policy applies to all visitors to our website, users of our services, and individuals whose personal information is collected, used, or processed by Right Spade in India.</p>

                            <h3 className="ls-h3">2. Information We Collect</h3>
                            <p className="ls-p">We collect the following categories of information per the IT Rules, 2011:</p>
                            <ul className="ls-ul">
                                <li><strong>Personal Information:</strong> Name, email address, phone number, address, and financial details like PAN card and bank information for KYC and payment processing.</li>
                                <li><strong>Sensitive Personal Data or Information (SPDI):</strong> This includes financial information (e.g., bank account or payment details), passwords, and KYC documents, as required for compliance with SEBI regulations.</li>
                                <li><strong>Non-Personal Information:</strong> Browser type, device information, IP address, and usage data for analytics and improving user experience.</li>
                            </ul>

                            <h3 className="ls-h3">3. Consent</h3>
                            <p className="ls-p">By using our website and providing your personal or sensitive information, you expressly consent to the collection, processing, and storage of your information as outlined in this Privacy Policy.</p>
                            <ul className="ls-ul">
                                <li>If you do not wish to share your information, please refrain from using our services or contact us for clarification.</li>
                                <li>You may withdraw your consent at any time, subject to legal and contractual obligations.</li>
                            </ul>

                            <h3 className="ls-h3">4. Use of Information</h3>
                            <p className="ls-p">We collect and use your information for the following purposes:</p>
                            <ul className="ls-ul">
                                <li>To verify your identity and perform KYC checks in compliance with SEBI regulations.</li>
                                <li>To provide access to our equity research services and manage your subscription.</li>
                                <li>To send transactional emails and notifications about your account.</li>
                                <li>To analyse website usage and improve our services.</li>
                                <li>To comply with legal obligations under Indian law and SEBI guidelines.</li>
                            </ul>

                            <h3 className="ls-h3">5. Disclosure of Information</h3>
                            <p className="ls-p">Your personal and sensitive information will not be shared with third parties, except:</p>
                            <ul className="ls-ul">
                                <li><strong>For Compliance:</strong> To comply with legal obligations, court orders, or government requests under Indian law.</li>
                                <li><strong>With Service Providers:</strong> Trusted third-party providers assisting in operations such as payment processing, data storage, and KYC verification.</li>
                                <li><strong>With Consent:</strong> When you explicitly consent to such disclosure.</li>
                            </ul>

                            <h3 className="ls-h3">6. Data Protection and Security</h3>
                            <p className="ls-p">In accordance with Section 43A of the IT Act and IT Rules, 2011, we follow Reasonable Security Practices and Procedures to protect your information, including:</p>
                            <ul className="ls-ul">
                                <li>Data encryption and secure server storage.</li>
                                <li>Role-based access control to limit employee access to sensitive data.</li>
                                <li>Regular audits and updates to our security systems to safeguard against unauthorised access, alteration, or destruction of data.</li>
                            </ul>

                            <h3 className="ls-h3">7. Retention of Information</h3>
                            <p className="ls-p">We retain your personal and sensitive information for as long as required to provide our services, comply with regulatory obligations, or resolve disputes. Once this data is no longer needed, we securely delete or anonymise it.</p>

                            <h3 className="ls-h3">8. Your Rights Under Indian Law</h3>
                            <p className="ls-p">Under Indian laws, including the IT Rules, 2011, you have the following rights concerning your information:</p>
                            <ul className="ls-ul">
                                <li><strong>Access and Correction:</strong> You may access your personal information and request corrections to any inaccurate or incomplete data.</li>
                                <li><strong>Withdrawal of Consent:</strong> You may withdraw your consent to the processing of your data, subject to legal obligations.</li>
                                <li><strong>Grievance Redressal:</strong> You can contact our Grievance Officer to address any complaints or concerns regarding your data.</li>
                            </ul>

                            <h3 className="ls-h3">9. Grievance Officer</h3>
                            <p className="ls-p">In compliance with Rule 5(9) of the IT Rules, 2011, we have appointed a Grievance Officer to address user grievances related to data protection and privacy.</p>
                            <ul className="ls-ul">
                                <li>Name: Shreyas Pandit</li>
                                <li>Email: <a href="mailto:care@rightspade.com" className="ls-link">care@rightspade.com</a></li>
                                <li>Phone: +91-6363219981</li>
                                <li>Address: 1008, 3rd Floor, 7th A Main, Koramangala 1st Block, Bangalore 560034</li>
                            </ul>

                            <h3 className="ls-h3">10. Cookies and Tracking Technologies</h3>
                            <ul className="ls-ul">
                                <li>We use cookies to enhance user experience and analyse website traffic.</li>
                                <li>By using our website, you consent to the use of cookies. You may manage or disable cookies in your browser settings, but doing so may limit access to certain features.</li>
                            </ul>

                            <h3 className="ls-h3">11. Regulatory and Legal Compliance</h3>
                            <p className="ls-p">Right Spade complies with applicable Indian laws, including the IT Act, SEBI regulations, and other related guidelines, to ensure the protection of user data.</p>

                            <h3 className="ls-h3">12. Changes to this Privacy Policy</h3>
                            <p className="ls-p">Right Spade reserves the right to modify or update this Privacy Policy at any time in compliance with applicable laws. Changes will be notified via the website, and continued use of our services after such changes constitutes your agreement to the updated policy.</p>

                            <h3 className="ls-h3">13. Governing Law</h3>
                            <p className="ls-p">This Privacy Policy and your use of our services shall be governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Bangalore.</p>

                            <h3 className="ls-h3">14. Contact</h3>
                            <p className="ls-p">If you have any questions or concerns about this Privacy Policy or the way your information is handled, please contact us:</p>
                            <ul className="ls-ul">
                                <li>Email: <a href="mailto:hello@rightspade.com" className="ls-link">hello@rightspade.com</a></li>
                                <li>Address: 1008, 3rd Floor, 7th A Main, Koramangala 1st Block, Bangalore 560034</li>
                            </ul>
                        </section>

                        {/* ══════════════════════════════════════════════════
                            9. TERMS & CONDITIONS
                        ══════════════════════════════════════════════════ */}
                        <section id="terms-and-conditions" className="legal-section">
                            <h2 className="legal-section-title">Terms and Conditions of Website Usage</h2>

                            <h3 className="ls-h3">1. Introduction</h3>
                            <p className="ls-p">Welcome to Right Spade Capital Advisors Private Limited (hereinafter referred to as "Right Spade"). By accessing or using this Website, you acknowledge that you have read, understood, and agreed to be bound by these Terms and Conditions. If you do not agree to these Terms, you must not use our Website or services.</p>

                            <h3 className="ls-h3">2. Scope of Services</h3>
                            <p className="ls-p">Right Spade provides equity market research and analysis services to empower smarter investment decisions. These services include stock recommendations and other market insights based on fundamental and technical research. We are registered with SEBI as a Research Analyst (RA) and strictly operate within the regulations prescribed by SEBI.</p>

                            <h3 className="ls-h3">3. Disclaimer: Investments are Subject to Risk</h3>
                            <ul className="ls-ul">
                                <li><strong>Market Risks:</strong> Investments in securities and financial markets involve substantial risk. The value of investments may go up or down due to various factors, including market volatility, economic changes, and individual company performance. Past performance is not indicative of future returns.</li>
                                <li><strong>No Guarantees:</strong> Right Spade does not guarantee any specific outcomes, returns, or profits.</li>
                                <li><strong>User Responsibility:</strong> You are solely responsible for your investment decisions. We strongly recommend that you conduct your own independent research or seek advice from a certified financial advisor before acting on any recommendations.</li>
                            </ul>

                            <h3 className="ls-h3">4. SEBI Compliance</h3>
                            <ul className="ls-ul">
                                <li>Right Spade strictly adheres to all SEBI (Securities and Exchange Board of India) guidelines and operates solely within the framework of SEBI regulations.</li>
                                <li>Any information, recommendations, or research shared by Right Spade and its team are compliant with SEBI's Code of Conduct and Disclosure Requirements for Research Analysts.</li>
                                <li>We do not endorse or promote unregulated investment opportunities or any financial instruments not approved by SEBI.</li>
                            </ul>

                            <h3 className="ls-h3">5. Independence and Integrity</h3>
                            <p className="ls-p">Right Spade, its promoters, employees, and representatives maintain full independence and impartiality in their analysis and recommendations.</p>
                            <ul className="ls-ul">
                                <li><strong>No Endorsements:</strong> We do not endorse or promote third-party financial products, unregulated schemes, or services outside the purview of SEBI guidelines.</li>
                                <li><strong>Conflict of Interest:</strong> Any potential conflicts of interest in our research will be disclosed as required by SEBI regulations.</li>
                            </ul>

                            <h3 className="ls-h3">6. Limitation of Liability</h3>
                            <p className="ls-p">Right Spade, its affiliates, promoters, employees, or agents shall not be liable for any direct, indirect, incidental, or consequential losses, damages, or expenses arising from:</p>
                            <ul className="ls-ul">
                                <li>Your use of the Website or reliance on the information provided.</li>
                                <li>Market conditions or decisions made based on our research and recommendations.</li>
                            </ul>
                            <p className="ls-p">You agree to indemnify and hold us harmless from any claims or damages resulting from your use of our Website or services.</p>

                            <h3 className="ls-h3">7. Intellectual Property</h3>
                            <p className="ls-p">All content on this Website, including text, images, research reports, logos, and designs, is the exclusive property of Right Spade or its licensors. Unauthorised use, reproduction, or distribution of this content is strictly prohibited and may result in legal action.</p>

                            <h3 className="ls-h3">8. Privacy Policy</h3>
                            <p className="ls-p">Your privacy is important to us. Our collection, use, and storage of personal data are governed by our Privacy Policy. By using this Website, you agree to the terms outlined in our Privacy Policy.</p>

                            <h3 className="ls-h3">9. Modifications to Terms and Conditions</h3>
                            <p className="ls-p">Right Spade reserves the right to modify, update, or replace these Terms at any time without prior notice. The updated Terms will take effect immediately upon being posted on this Website. It is your responsibility to review these Terms periodically.</p>

                            <h3 className="ls-h3">10. Third-Party Links</h3>
                            <p className="ls-p">The Website may contain links to third-party websites for your convenience. Right Spade is not responsible for the content, accuracy, or policies of any external sites and does not endorse or assume liability for any third-party services.</p>

                            <h3 className="ls-h3">11. Subscription and Payment Terms</h3>
                            <ul className="ls-ul">
                                <li>By subscribing to our services, you agree to pay the applicable subscription fees and comply with the payment terms specified on the Website.</li>
                                <li>Access to services is subject to the completion of KYC submission, execution of the service agreement and required customer onboarding compliances.</li>
                                <li>Refunds for cancellations, if applicable, will be governed by the terms of the service agreement and will be processed within a maximum of 30 business days, from the date of both parties agreeing to the refund amount.</li>
                            </ul>

                            <h3 className="ls-h3">12. Governing Law and Dispute Resolution</h3>
                            <p className="ls-p">These Terms and Conditions are governed by the laws of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.</p>

                            <h3 className="ls-h3">13. Contact Information</h3>
                            <p className="ls-p">
                                If you have any questions about these Terms, please contact us at:{' '}
                                <a href="mailto:hello@rightspade.com" className="ls-link">hello@rightspade.com</a>
                            </p>
                        </section>

                    </Col>{/* end main content */}
                </Row>
            </Container>
        </div>
    );
};

export default LegalPage;
